import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import Highcharts from 'highcharts';

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.scss'
})
export class ChartComponent implements OnInit, OnChanges {
  chart: Highcharts.Chart | undefined;
  chartContainerId: string = 'chartContainer';
  chartConfig: Highcharts.Options | undefined;
  activeButtonIndex: number | undefined = 0;
  seriesHidden: boolean = false;
  initTotalDisabled: boolean = true;
  hasData: boolean = true;
  grandTotal: number = 0;
  scatterMode: boolean = false;
  showScatterToggle: boolean = false;

  @Input() config: Highcharts.Options | undefined;
  @Input() hideButton?: boolean = false;
  @Input() isDetail?: boolean = false;
  @Input() customButtons?: any = [];
  @Input() hideGrandTotal: boolean = false;

  @Output() clickEmitter = new EventEmitter<any>();


  constructor(private translocoSrv: TranslocoService) {}
  
  ngOnInit(): void {
    this.chartContainerId = this.isDetail ? 'detailChartContainer' : 'chartContainer';
    this.createChart();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if(changes['config']?.currentValue || this.isDetail) {
      this.createChart();
      this.seriesHidden = false;
      this.scatterMode = false;
    }
  }

  async createChart() {
    this.chart?.destroy();
    this.chart = undefined;

    if (!this.config) {
      this.chartConfig = undefined;
      this.hasData = false;
      return;
    }

    if (this.initTotalDisabled) this.disableTotal(this.config);
    if (this.config.subtitle) this.config.subtitle.x = 0;
    this.chartConfig = { ...this.config };
    this.chartConfig.accessibility = { enabled: false };
    this.hasData = this.computeHasData(this.chartConfig);

    if (!this.hasData) {
      return;
    }

    this.injectSummaries(this.chartConfig);
    this.injectFocusMode(this.chartConfig);
    await this.hideCredits();
    this.chart = Highcharts.chart(this.chartContainerId, this.chartConfig);
  }

  disableTotal(config: Highcharts.Options) {
    const totalSerie = config.series?.find(serie => serie.name?.toLowerCase() === 'total');
    if (totalSerie) totalSerie.visible = false;
  }

  addCulture() {
    if(this.chartConfig) {
      this.chartConfig.lang = {
        decimalPoint: ',',
        thousandsSep: '.',
        months: [
          'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
          'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
        ],
        weekdays: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
        shortMonths: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
        loading: 'Cargando...',
        noData: 'No hay datos para mostrar',
        // locale: 'es'
      }
    }
  }
    // tooltip: {
    //   formatter: function () {
    //     return `<b>${this.series.name}</b><br>
    //       <span>Fecha: ${Highcharts.dateFormat('%A, %e de %B de %Y', this.x as number)}</span><br>
    //       <span>Valor: ${this.y}</span>`;
    //   }
    // },
  // }

  hideCredits() {
    if(this.chartConfig) {

      this.chartConfig.credits = {
        enabled: false  // Disable library logo and link
      }
    }
  }

  customButtonClicked(buttonIndex: number) {
    this.activeButtonIndex = buttonIndex;
    this.clickEmitter.emit(buttonIndex);
  }

  toggleScatterMode(): void {
    if (!this.chart) return;
    this.scatterMode = !this.scatterMode;

    this.chart.series.forEach(serie => {
      serie.update(
        this.scatterMode
          ? { lineWidth: 0, marker: { enabled: true, radius: 5 }, states: { hover: { lineWidthPlus: 2 } } } as Highcharts.SeriesLineOptions
          : { lineWidth: 2, marker: { enabled: false }, states: { hover: { lineWidthPlus: 2 } } } as Highcharts.SeriesLineOptions,
        false
      );
    });
    this.chart.redraw();
  }

  hideAllSeries() {
    const allHidden = this.chart?.series.every(serie => !serie.visible);
    this.seriesHidden = !this.seriesHidden;

    this.chart?.series.forEach(serie => {
      if (allHidden || serie.visible) {
        serie.setVisible(!serie.visible, false);
      }
    });

    this.chart?.redraw();
  }
  private injectFocusMode(config: Highcharts.Options): void {
    const seriesCount = Array.isArray(config.series) ? config.series.length : 0;
    this.showScatterToggle = seriesCount > 1;

    if (seriesCount <= 7) return;

    if (!config.plotOptions) config.plotOptions = {};
    if (!config.plotOptions.series) config.plotOptions.series = {};
    config.plotOptions.series.states = {
      hover: { lineWidthPlus: 2 },
      inactive: { opacity: 0.35 }
    };

    config.tooltip = {
      shared: false,
      headerFormat: '<span style="font-size:11px">{point.x:%d/%m/%Y}</span><br/>',
      pointFormat: '<b>{series.name}</b>: {point.y}<br/>'
    };
  }

  private injectSummaries(config: Highcharts.Options): void {
    this.grandTotal = 0;
    if (!config.series || !Array.isArray(config.series)) return;

    config.series = config.series.map(s => {
      const series = { ...(s as Highcharts.SeriesLineOptions) };
      const sourceData = series.data;
      if (!Array.isArray(sourceData) || sourceData.length === 0) return series;

      const sum = sourceData.reduce((acc: number, point: any) => {
        const value = Array.isArray(point) ? point[1] : (point?.y ?? 0);
        return acc + (Number(value) || 0);
      }, 0);

      if (series.visible !== false) {
        this.grandTotal += sum;
      }

      if (sum > 0 && series.name) {
        series.name = `${series.name} (${sum.toLocaleString()})`;
      }

      return series as Highcharts.SeriesOptionsType;
    });
  }

  private computeHasData(config: Highcharts.Options): boolean {
    const series = config.series as any[] | undefined;
    if (!Array.isArray(series) || series.length === 0) {
      return false;
    }

    for (const item of series) {
      const data = item?.data;
      if (Array.isArray(data) && data.length > 0) {
        return true;
      }
    }

    return false;
  }

}
